<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouterStore } from '@/store/router'
import { useInformeTokens } from '@/composables/useInformeTokens'
import '@/assets/mayores-theme.css'
import MayoresEncabezado from '@/components/SalidaPublicaMayores/MayoresEncabezado.vue'
import InformeElegirConductor from '@/components/InformeConductor/InformeElegirConductor.vue'
import InformeSalidas from '@/components/InformeConductor/InformeSalidas.vue'
import InformeTerritorios from '@/components/InformeConductor/InformeTerritorios.vue'
import { fechaTexto } from '@/components/InformeConductor/fecha'

// Enlace público para que cada conductor informe qué territorios se trabajaron
// en sus salidas. Sólo existe en la vista para mayores ("Portal Fácil").

const route = useRouterStore()
const { fetchPublicInforme, postInforme, getMapaUrl } = useInformeTokens()

const mapaUrl = (terrKey, mzIdx) => getMapaUrl(token.value, terrKey, mzIdx)

const token = computed(() => route.params.token)

// ─── Preferencias de accesibilidad (compartidas con la salida para mayores) ─
const textScale = ref(Number(localStorage.getItem('mayores-text-scale')) || 1)
watch(textScale, (v) => localStorage.setItem('mayores-text-scale', v))
const altoContraste = ref(localStorage.getItem('mayores-alto-contraste') === '1')

// ─── Datos ───────────────────────────────────────────────────────────────────
const loading = ref(true)
const errorMsg = ref(null)
const data = ref(null)

const recordadoKey = computed(() => `informe-conductor-${token.value}`)
const recordadoId = ref(null)

// ─── Navegación: conductor y salida viven en la URL (atrás/adelante funciona) ─
const setNavParams = (updates) => {
  const next = { ...route.params }
  for (const [k, v] of Object.entries(updates)) {
    if (v == null) delete next[k]
    else next[k] = v
  }
  route.params = next
}

const conductor = computed(
  () => data.value?.conductores?.find((c) => c.id === route.params.conductor) || null
)
const salida = computed(
  () => conductor.value?.salidas.find((s) => String(s.id) === String(route.params.salida)) || null
)

// Resumen del último informe enviado, para la pantalla de confirmación.
const enviado = ref(null)

const pantalla = computed(() => {
  if (enviado.value) return 'enviado'
  if (salida.value) return 'salida'
  if (conductor.value) return 'salidas'
  return 'conductor'
})

const titulo = computed(() => {
  if (pantalla.value === 'salida') return 'Informar salida'
  if (pantalla.value === 'salidas') return 'Mis salidas'
  return 'Informe de salidas'
})

const elegirConductor = (id) => {
  recordadoId.value = id
  try { localStorage.setItem(recordadoKey.value, id) } catch { /* noop */ }
  setNavParams({ conductor: id, salida: null })
}

const cambiarConductor = () => setNavParams({ conductor: null, salida: null })

const informar = (salidaId) => setNavParams({ salida: salidaId })

const volver = () => {
  if (pantalla.value === 'enviado') { seguir(); return }
  if (pantalla.value === 'salida') { setNavParams({ salida: null }); return }
  if (pantalla.value === 'salidas') cambiarConductor()
}

watch(() => [route.params.conductor, route.params.salida], () => { enviado.value = null })

// ─── Envío ───────────────────────────────────────────────────────────────────
const enviando = ref(false)
const errorEnvio = ref(null)
const territoriosRef = ref(null)

const enviar = async ({ territorios, notas }) => {
  if (!salida.value || !conductor.value) return
  enviando.value = true
  errorEnvio.value = null
  let res
  try {
    res = await postInforme(token.value, salida.value.id, {
      conductorId: conductor.value.id,
      territorios,
      notas,
    })
  } catch {
    res = { error: 'No hay conexión. Revisá internet y probá de nuevo.' }
  }
  enviando.value = false

  // "Ya fue informada" también cierra la salida: no tiene sentido reintentar.
  if (res.error && !/ya fue informada/i.test(res.error)) {
    errorEnvio.value = res.error
    return
  }

  territoriosRef.value?.limpiarBorrador()
  const informada = salida.value
  conductor.value.salidas = conductor.value.salidas.filter((s) => s.id !== informada.id)
  enviado.value = {
    fecha: fechaTexto(informada.date),
    trabajados: territorios
      .filter((t) => t.trabajado)
      .map((t) => (t.manzanas?.length ? `${t.key} (manzana${t.manzanas.length !== 1 ? 's' : ''} ${t.manzanas.join(', ')})` : t.key)),
    noTrabajados: territorios.filter((t) => !t.trabajado).map((t) => t.key),
    yaInformada: Boolean(res.error),
  }
}

// El parámetro `salida` queda en la URL mientras se muestra la confirmación;
// al quitarlo, el watcher de más arriba también descarta `enviado`.
const seguir = () => {
  enviado.value = null
  setNavParams({ salida: null })
}

// ─── Carga inicial ───────────────────────────────────────────────────────────
onMounted(async () => {
  if (!token.value) {
    errorMsg.value = 'Este enlace está incompleto. Pedí el enlace de nuevo a quien te lo envió.'
    loading.value = false
    return
  }
  try {
    const res = await fetchPublicInforme(token.value)
    if (res.error) {
      errorMsg.value = res.error
    } else {
      data.value = res
      const guardado = localStorage.getItem(recordadoKey.value)
      if (guardado && res.conductores.some((c) => c.id === guardado)) {
        recordadoId.value = guardado
        // Quien ya usó el enlace en este teléfono entra directo a sus salidas.
        if (!route.params.conductor) setNavParams({ conductor: guardado })
      }
    }
  } catch {
    errorMsg.value = 'No se pudieron cargar las salidas. Revisá internet y probá de nuevo.'
  }
  loading.value = false
})
</script>

<template>
  <div class="mayores-app" :class="{ 'alto-contraste': altoContraste }" :style="{ '--text-scale': textScale }">
    <MayoresEncabezado
      :titulo="titulo"
      :mostrar-volver="!loading && !errorMsg && pantalla !== 'conductor'"
      :text-scale="textScale"
      :alto-contraste="altoContraste"
      :mostrar-cambiar-vista="false"
      @volver="volver"
      @update:text-scale="textScale = $event"
    />

    <div v-if="loading" class="mayores-estado">
      <i class="pi pi-spin pi-spinner mayores-estado__icono" aria-hidden="true" />
      <p>Cargando...</p>
    </div>

    <div v-else-if="errorMsg" class="mayores-estado">
      <i class="pi pi-lock mayores-estado__icono" aria-hidden="true" />
      <p>{{ errorMsg }}</p>
    </div>

    <template v-else>
      <InformeElegirConductor
        v-if="pantalla === 'conductor'"
        :conductores="data.conductores"
        :recordado-id="recordadoId"
        :congregacion="data.congregacion"
        @elegir="elegirConductor"
      />

      <InformeSalidas
        v-else-if="pantalla === 'salidas'"
        :conductor="conductor"
        @informar="informar"
        @cambiar-conductor="cambiarConductor"
      />

      <InformeTerritorios
        v-else-if="pantalla === 'salida'"
        ref="territoriosRef"
        :key="salida.id"
        :salida="salida"
        :mapa-url="mapaUrl"
        :enviando="enviando"
        :error="errorEnvio"
        @enviar="enviar"
      />

      <div v-else-if="pantalla === 'enviado'" class="mayores-contenido">
        <div class="tarjeta-accion mayores-exito mayores-exito-panel" role="status">
          <i class="pi pi-check-circle" aria-hidden="true" />
          <h2>{{ enviado.yaInformada ? 'Esta salida ya estaba informada' : '¡Gracias! Informe enviado' }}</h2>
          <p>{{ enviado.fecha }}</p>
        </div>

        <template v-if="!enviado.yaInformada">
          <p v-if="enviado.trabajados.length" class="tarjeta-lista__detalle">
            <i class="pi pi-check" aria-hidden="true" />
            Se trabajó: <strong>{{ enviado.trabajados.join('; ') }}</strong>
          </p>
          <p v-if="enviado.noTrabajados.length" class="tarjeta-lista__detalle">
            <i class="pi pi-times" aria-hidden="true" />
            No se trabajó: <strong>{{ enviado.noTrabajados.join(', ') }}</strong>
          </p>
        </template>

        <button type="button" class="boton-primario mayores-separacion" @click="seguir">
          <i class="pi pi-list" aria-hidden="true" />
          {{ conductor?.salidas.length ? 'Informar otra salida' : 'Volver a mis salidas' }}
        </button>
      </div>
    </template>
  </div>
</template>
